import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "operatore_contabile", label: "Operatore contabile" },
  { value: "operatore_flotta", label: "Operatore flotta" },
  { value: "sola_lettura", label: "Sola lettura" },
];

export default function Utenti() {
  const [users, setUsers] = useState([]);
  const { user: me } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatErr(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/users", form); toast.success("Utente creato"); setOpen(false); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const setRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-black">Utenti</h1>
        <Button size="sm" className="h-8 gap-1" onClick={() => { setForm({ role: "operatore_contabile" }); setOpen(true); }} data-testid="users-new"><Plus className="w-4 h-4" />Nuovo utente</Button>
      </div>
      <Card className="rounded-md overflow-hidden">
        <table className="autotrans-table w-full" data-testid="users-table">
          <thead><tr><th>Username</th><th>Nome</th><th>Email</th><th>Ruolo</th><th>Aziende</th><th>2FA</th><th>Stato</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td className="font-mono">{u.username}</td><td>{u.full_name}</td><td>{u.email}</td>
                <td><Select value={u.role} onValueChange={(v) => setRole(u, v)} disabled={u.id === me?.id}>
                  <SelectTrigger className="h-7 w-48" data-testid={`users-role-${u.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select></td>
                <td className="text-xs">{u.companies?.length ? u.companies.join(", ") : "Tutte"}</td>
                <td>{u.two_factor_enabled ? <Badge className="bg-green-600 h-5">Attiva</Badge> : <Badge variant="outline" className="h-5">No</Badge>}</td>
                <td>{u.id === me?.id ? <span className="text-xs text-muted-foreground">Tu</span> :
                  <Button size="sm" variant="outline" className="h-7" onClick={() => toggleActive(u)} data-testid={`users-toggle-${u.id}`}>{u.is_active ? "Disattiva" : "Attiva"}</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo utente</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[["username", "Username"], ["full_name", "Nome completo"], ["email", "Email"], ["password", "Password"]].map(([k, l]) => (
              <div key={k}><Label className="text-xs font-heading uppercase">{l}</Label>
                <Input className="h-8 mt-1" type={k === "password" ? "password" : "text"} value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`users-field-${k}`} /></div>
            ))}
            <div><Label className="text-xs font-heading uppercase">Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="h-8 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="col-span-2"><Label className="text-xs font-heading uppercase">Aziende accessibili (nessuna = tutte)</Label>
              <div className="flex gap-4 mt-2">
                {[["golmar", "Golmar"], ["vitam", "Vitam"], ["rotta66", "ROTTA66"], ["fmg", "Consorzio FMG"]].map(([c, l]) => (
                  <label key={c} className="flex items-center gap-1.5 text-sm">
                    <Checkbox checked={(form.companies || []).includes(c)} data-testid={`users-co-${c}`}
                      onCheckedChange={(v) => setForm({ ...form, companies: v ? [...(form.companies || []), c] : (form.companies || []).filter((x) => x !== c) })} />{l}
                  </label>
                ))}
              </div></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} data-testid="users-save">Crea</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
